import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Navbar from './Navbar';
import { Product, ProductType } from '../types';
import ProductFilters from './ProductFilters';

const PriceEditor: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modifiedPrices, setModifiedPrices] = useState<{[key: number]: {price_net: number, price_alt?: number}}>({}); 
  const [usePrimaryPrice, setUsePrimaryPrice] = useState(true);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  
  // Replace the hook with direct state management
  const [filters, setFilters] = useState({
    name: '',
    brand: '',
    country: '',
    region: '',
    product_type_id: '',
    productType: 'all',
    category: '',
    category_id: '',
    subcategory_id: '',
    vintage: '',
    alcoholMin: '',
    alcoholMax: '',
    priceMin: '',
    priceMax: '',
    userId: 'all',
    grape_variety: '',
    characteristics: '',
    features: ''
  });

  // Fetch product types on component mount
  useEffect(() => {
    const fetchProductTypes = async () => {
      try {
        const response = await axios.get('/api/product-types');
        setProductTypes(response.data);
      } catch (err) {
        console.error('Error fetching product types:', err);
      }
    };
    
    fetchProductTypes();
  }, []);

  // Memoize fetchProducts to prevent recreation on each render
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Log all filters for debugging
      console.log('Applying filters:', JSON.stringify(filters, null, 2));

      // Fix parameter naming to match exactly what backend expects
      if (filters.product_type_id) params.append('product_type_id', filters.product_type_id);
      if (filters.name) params.append('name', filters.name);
      if (filters.brand) params.append('brand', filters.brand);
      if (filters.country) params.append('country', filters.country);
      if (filters.region) params.append('region', filters.region);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.subcategory_id) params.append('subcategory_id', filters.subcategory_id);
      if (filters.vintage) params.append('vintage', filters.vintage);
      if (filters.grape_variety) params.append('grape_variety', filters.grape_variety);
      if (filters.characteristics) params.append('characteristics', filters.characteristics);
      if (filters.features) params.append('features', filters.features);
      
      // Use correct parameter names for these (check your API)
      if (filters.alcoholMin) params.append('alcohol_content_min', filters.alcoholMin);
      if (filters.alcoholMax) params.append('alcohol_content_max', filters.alcoholMax);
      if (filters.userId && filters.userId !== 'all') params.append('user_id', filters.userId);

      const url = `/api/products?${params.toString()}`;
      console.log('API request URL:', url);

      const res = await axios.get(url);
      console.log(`Received ${res.data.length} products from API`);
      setProducts(res.data);
    } catch (err: any) {
      console.error('PriceEditor fetchProducts error', err);
      setError('Błąd pobierania produktów');
    } finally {
      setLoading(false);
    }
  }, [filters]); // Include filters in dependencies

  // Handle filter changes directly with more detailed logging
  const handleFilterChange = useCallback((field: string, value: string) => {
    console.log(`Filter changed: ${field} = ${value}`);
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      return newFilters;
    });
  }, []);
  
  // Handle product type change with clear logging
  const handleProductTypeChange = useCallback((typeId: string) => {
    console.log(`Product type changed to: ${typeId}`);
    setFilters(prev => ({
      ...prev,
      product_type_id: typeId,
      productType: typeId || 'all',
      // Reset dependent fields
      category_id: '',
      subcategory_id: ''
    }));
  }, []);

  // Fetch products when filters change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const getProductTypeDisplayName = (typeId?: number | string | null) => {
    // Add debug logging
    console.log("Product type debug:", {
      value: typeId,
      type: typeof typeId,
      productTypesCount: productTypes.length
    });
    
    if (typeId === undefined || typeId === null) {
      return 'Nieznany';
    }
    
    // Check if we have product types loaded
    if (productTypes.length === 0) {
      console.warn("Product types array is empty!");
      // Return the raw value if no mapping is possible
      return String(typeId);
    }
    
    // Try to find by ID (when typeId is a number)
    if (!isNaN(Number(typeId))) {
      const productType = productTypes.find(pt => pt.id === Number(typeId));
      if (productType) {
        return productType.name;
      }
    }
    
    // Handle case where product.product_type_id is used instead
    const productById = products.find(p => p.id === Number(typeId));
    if (productById?.product_type_id) {
      const productType = productTypes.find(pt => pt.id === Number(productById.product_type_id));
      if (productType) {
        return productType.name;
      }
    }
    
    // Try using the direct name mapping as fallback
    const nameMap: { [key: string]: string } = {
      'wine': 'Wino',
      'cigar': 'Cygaro',
      'other': 'Inne',
      'spirit': 'Alkohol wysokoprocentowy',
      'gift': 'Zestaw prezentowy'
    };
    
    // Try lowercase matching
    const typeString = String(typeId).toLowerCase();
    if (nameMap[typeString]) {
      return nameMap[typeString];
    }
    
    // Return the original value if all else fails
    return String(typeId);
  };

  const handlePriceChange = (productId: number, field: 'price_net' | 'price_alt', value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    setModifiedPrices(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: numValue
      }
    }));
  };

  const savePrice = async (productId: number) => {
    if (!modifiedPrices[productId]) return;

    try {
      await axios.patch(`/api/products/${productId}`, modifiedPrices[productId]);
      toast.success('Cena została zaktualizowana');
      
      // Update local state to reflect saved changes
      setProducts(products.map(p => {
        if (p.id === productId) {
          return {...p, ...modifiedPrices[productId]};
        }
        return p;
      }));
      
      // Clear this product from modified prices
      const updatedPrices = {...modifiedPrices};
      delete updatedPrices[productId];
      setModifiedPrices(updatedPrices);
    } catch (err: any) {
      toast.error('Błąd podczas zapisywania ceny');
    }
  };

  const saveAllPrices = async () => {
    const productIds = Object.keys(modifiedPrices).map(Number);
    if (productIds.length === 0) return;

    try {
      const promises = productIds.map(id => 
        axios.patch(`/api/products/${id}`, modifiedPrices[id])
      );
      
      await Promise.all(promises);
      toast.success('Wszystkie ceny zostały zaktualizowane');
      
      // Update local state
      setProducts(products.map(p => {
        if (modifiedPrices[p.id]) {
          return {...p, ...modifiedPrices[p.id]};
        }
        return p;
      }));
      
      // Clear modified prices
      setModifiedPrices({});
    } catch (err) {
      toast.error('Wystąpił błąd podczas zapisywania cen');
    }
  };

  // REMOVE the old client-side filtering logic.
  // The server now handles all filtering.
  const filteredProducts = products.sort((a, b) => a.name.localeCompare(b.name));

  // Add this function to your component, similar to the one in ProductTable.tsx
  const getTypeClassName = (productTypeId?: number | string | null) => {
    if (!productTypeId) return '';
    
    // Convert to number if it's a string numeric value
    const typeId = typeof productTypeId === 'string' && !isNaN(Number(productTypeId)) 
      ? parseInt(productTypeId, 10) 
      : productTypeId;
    
    // If it's not a number (like a string code), map directly
    if (isNaN(Number(typeId))) {
      // Handle string-based type codes directly
      return String(productTypeId).toLowerCase();
    }
    
    // Look up by ID and map the name to a class
    const productType = productTypes.find(pt => pt.id === typeId);
    
    if (!productType) return '';
    
    // Map product type name to CSS class (simplify the name to lowercase and remove spaces)
    const typeName = productType.name?.toLowerCase() || '';
    
    // Map common product type names to CSS classes
    if (typeName.includes('wino') || typeName.includes('wine')) return 'wine';
    if (typeName.includes('spirit') || typeName.includes('alkohol')) return 'spirit';
    if (typeName.includes('cygar') || typeName.includes('cigar')) return 'cigar';
    if (typeName.includes('gift') || typeName.includes('zestaw')) return 'gift';
    
    // Default to product type name or 'other'
    return typeName || 'other';
  };

  if (loading) return <div className="loading">Ładowanie produktów...</div>;

  return (
    <>
      <Navbar />
      <div className="container">
        <div className="card">
          <div className="table-header">
            <div className="table-title">
              <h1>Szybka Edycja Cen</h1>
              <div className="product-count">{filteredProducts.length} produktów</div>
            </div>
            {/* Save All button moved outside table-actions for better visibility */}
          </div>

          <div className="table-filters">
            <ProductFilters
              filters={filters}
              onChange={handleFilterChange}
              onProductTypeChange={handleProductTypeChange}
              productTypes={productTypes}
              className="price-editor-filters"
            />
            
            <div className="price-editor-header">
              <div className="filter-group">
                <label>Aktywny cennik:</label>
                <div className="filter-buttons">
                  <button 
                    className={`btn btn-sm ${usePrimaryPrice ? 'btn-primary' : 'btn-outline'}`} 
                    onClick={() => setUsePrimaryPrice(true)}
                  >
                    Cennik podstawowy
                  </button>
                  <button 
                    className={`btn btn-sm ${!usePrimaryPrice ? 'btn-primary' : 'btn-outline'}`} 
                    onClick={() => setUsePrimaryPrice(false)}
                  >
                    Cennik alternatywny
                  </button>
                </div>
              </div>
              
              {/* Save All button positioned on the right */}
              <button 
                onClick={saveAllPrices} 
                className="btn btn-success"
                disabled={Object.keys(modifiedPrices).length === 0}
              >
                Zapisz wszystkie zmiany ({Object.keys(modifiedPrices).length})
              </button>
            </div>
          </div>

          {filteredProducts.length === 0 ? (
            <div className="empty-state">
              {filters.name ? (
                <div>
                  <h3>Brak wyników wyszukiwania</h3>
                  <p>Nie znaleziono produktów pasujących do "{filters.name}"</p>
                  <button className="btn btn-primary" onClick={() => handleFilterChange('name', '')}>
                    Wyczyść filtr
                  </button>
                </div>
              ) : (
                <div>
                  <h3>Brak produktów</h3>
                  <p>Dodaj pierwszy produkt do bazy danych!</p>
                </div>
              )}
            </div>
          ) : (
            <div className="table-container">
              <table className="product-table price-editor-table">
                <thead>
                  <tr>
                    <th className="col-thumb"><span className="sr-only">Zdjęcie</span></th>
                    <th>Nazwa produktu</th>
                    <th>Typ</th>
                    <th>Marka</th>
                    <th className="col-price">Cena podstawowa (zł)</th>
                    <th className="col-price">Cena alternatywna (zł)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => {
                    const isModified = !!modifiedPrices[product.id];
                    
                    return (
                      <tr key={product.id} className={`product-row ${isModified ? 'modified-row' : ''}`}>
                        {/* ...existing table cell code... */}
                        <td>
                          {product.image_url && (
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="table-thumbnail"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          )}
                        </td>
                        <td className="product-name">
                          <strong>{product.name}</strong>
                        </td>
                        <td>
                          <span className={`type-badge ${getTypeClassName(product.product_type_id)}`}>
                            {getProductTypeDisplayName(product.product_type_id)}
                          </span>
                        </td>
                        <td>{product.brand || 'N/A'}</td>
                        <td>
                          <div className="price-input-group">
                            <input 
                              type="number" 
                              className={`form-control ${isModified && modifiedPrices[product.id].price_net !== undefined ? 'modified-input' : ''}`}
                              value={modifiedPrices[product.id]?.price_net !== undefined 
                                ? modifiedPrices[product.id].price_net 
                                : product.price_net || ''}
                              onChange={(e) => handlePriceChange(product.id, 'price_net', e.target.value)}
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </td>
                        <td>
                          <div className="price-input-group">
                            <input 
                              type="number" 
                              className={`form-control ${isModified && modifiedPrices[product.id].price_alt !== undefined ? 'modified-input' : ''}`}
                              value={modifiedPrices[product.id]?.price_alt !== undefined 
                                ? modifiedPrices[product.id].price_alt 
                                : product.price_alt || ''}
                              onChange={(e) => handlePriceChange(product.id, 'price_alt', e.target.value)}
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </td>
                        {/* Remove individual save buttons to encourage batch saving */}
                        {/* <td>
                          <button
                            onClick={() => savePrice(product.id)}
                            disabled={!isModified}
                            className="btn btn-sm btn-primary"
                          >
                            Zapisz
                          </button>
                        </td> */}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PriceEditor;